import { useEffect, useState } from 'react'
import { clearLocalData } from '../api/clientId'

const DISMISSED_KEY = 'en-privacy-notice-dismissed'

/**
 * One-time privacy disclosure shown on first visit. Floating dismissible
 * card at the bottom-right of the viewport. Doesn't block interaction —
 * the user can keep working with the playground while it's visible.
 *
 * Stays dismissed forever once "Got it" is clicked (the dismissal flag
 * lives in localStorage alongside the install ID itself, so clearing
 * local data resets it to first-visit state).
 *
 * The notice exists to make the localStorage usage *transparent* without
 * the friction of a blocking cookie banner. Strict-necessity argument
 * under ePrivacy Art 5(3) covers the persistence; this component is the
 * affirmative disclosure that legitimizes the no-banner posture by
 * showing the user what's stored and giving them a one-click reset.
 *
 * See: client/landing/src/pages/PrivacyPage.tsx for the full policy.
 */
export function PrivacyNotice() {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        try {
            const dismissed = localStorage.getItem(DISMISSED_KEY) === 'true'
            if (!dismissed) {
                setVisible(true)
            }
        } catch {
            // localStorage unavailable — show the notice anyway, no worse
            // than dismissing it would be
            setVisible(true)
        }
    }, [])

    const handleDismiss = () => {
        try {
            localStorage.setItem(DISMISSED_KEY, 'true')
        } catch {
            // ignore — user just won't see it after this page lifecycle
        }
        setVisible(false)
    }

    const handleClear = () => {
        clearLocalData()
        // Reload so Zustand store and theme context reset to defaults
        window.location.reload()
    }

    if (!visible) return null

    return (
        <div
            className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-xl shadow-2xl"
            style={{
                background: 'var(--surface-elevated)',
                border: '1px solid var(--border)',
                padding: '14px 16px',
            }}
            role="dialog"
            aria-label="Privacy notice"
        >
            <div className="text-[11px] text-text-secondary leading-relaxed">
                Endiagram stores your editor state and a random install ID in this
                browser so your work persists across reloads. We log request
                metadata (HMAC of your IP, country, browser family) to operate the
                service. <strong>No ad trackers, no third-party analytics.</strong>
            </div>
            <div className="mt-3 flex items-center gap-3">
                <a
                    href="https://endiagram.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] uppercase tracking-widest text-accent hover:underline"
                >
                    Privacy policy
                </a>
                <span className="text-[10px] text-text-muted">·</span>
                <button
                    onClick={handleClear}
                    title="Right to erasure: delete the install ID and editor state stored in this browser"
                    className="text-[10px] uppercase tracking-widest text-text-muted hover:text-accent transition-colors"
                >
                    Clear local data
                </button>
                <span className="flex-1" />
                <button
                    onClick={handleDismiss}
                    className="text-[10px] uppercase tracking-widest font-bold text-accent hover:text-text transition-colors"
                >
                    Got it
                </button>
            </div>
        </div>
    )
}
